/**
 * Parse Laravel AI SDK SSE stream (JSON events in data: lines).
 */
import { mapCommerceMedia } from './commerce-media.js';

/** Belső LLM jelzők — soha ne jelenjenek meg a látogatói buborékban. */
const INTERNAL_MARKER_COMPLETE = /%%(?:HANDOVER_REQUEST(?::\{[\s\S]*?\})?|LEAD_FORM:\{[\s\S]*?\})%%/g;
const INTERNAL_MARKER_INCOMPLETE = /%%(?:HANDOVER_REQUEST(?::\{[\s\S]*)?|LEAD_FORM:\{[\s\S]*)?$/;

function stripInternalMarkersForDisplay(text) {
    let cleaned = text.replace(INTERNAL_MARKER_COMPLETE, '');
    cleaned = cleaned.replace(INTERNAL_MARKER_INCOMPLETE, '');

    return cleaned;
}

function processSseDataLine(
    payload,
    assistantText,
    onDelta,
    onTurnPersisted,
    onContentReplacement,
    onLeadForm,
    onToolCallStart,
    onToolCallResult,
    onHandoverStatus,
) {
    if (payload === '[DONE]') {
        return { assistantText, streamDone: true };
    }

    if (payload === '') {
        return { assistantText, streamDone: false };
    }

    try {
        const event = JSON.parse(payload);

        if (event.type === 'text_delta' && typeof event.delta === 'string') {
            assistantText += event.delta;
            onDelta?.(stripInternalMarkersForDisplay(assistantText), event.delta);
        } else if (event.type === 'turn_persisted') {
            onTurnPersisted?.({
                assistantMessageId: event.assistant_message_id,
                userMessageId: event.user_message_id,
                sourceAttribution: event.source_attribution?.text ?? null,
                commerceMedia: mapCommerceMedia(event.commerce_media),
            });
        } else if (event.type === 'content_replacement' && typeof event.content === 'string') {
            assistantText = event.content;
            onContentReplacement?.(event.content);
            onDelta?.(stripInternalMarkersForDisplay(assistantText), '');
        } else if (event.type === 'lead_form' && event.form_id && event.form_payload) {
            onLeadForm?.({
                form_id: event.form_id,
                form_payload: event.form_payload,
            });
        } else if (event.type === 'handover_status') {
            onHandoverStatus?.(event);
        } else if (event.type === 'tool_call_start') {
            onToolCallStart?.(event);
        } else if (event.type === 'tool_call_result') {
            onToolCallResult?.(event);
        }
    } catch {
        // non-JSON data line — ignore
    }

    return { assistantText, streamDone: false };
}

function processSseLines(
    lines,
    assistantText,
    onDelta,
    onTurnPersisted,
    onContentReplacement,
    onLeadForm,
    onToolCallStart,
    onToolCallResult,
    onHandoverStatus,
) {
    let streamDone = false;

    for (const line of lines) {
        if (line.startsWith(':')) {
            continue;
        }

        if (! line.startsWith('data:')) {
            continue;
        }

        const result = processSseDataLine(
            line.slice(5).trim(),
            assistantText,
            onDelta,
            onTurnPersisted,
            onContentReplacement,
            onLeadForm,
            onToolCallStart,
            onToolCallResult,
            onHandoverStatus,
        );

        assistantText = result.assistantText;

        if (result.streamDone) {
            streamDone = true;
        }
    }

    return { assistantText, streamDone };
}

export async function consumeChatStream(
    response,
    onDelta,
    onDone,
    onTurnPersisted,
    onContentReplacement,
    onLeadForm,
    onToolCallStart = null,
    onToolCallResult = null,
    onHandoverStatus = null,
) {
    if (! response.body) {
        throw new Error('A válasz nem streamelhető.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let assistantText = '';
    let streamDone = false;

    while (true) {
        const { done, value } = await reader.read();

        if (value) {
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            const result = processSseLines(
                lines,
                assistantText,
                onDelta,
                onTurnPersisted,
                onContentReplacement,
                onLeadForm,
                onToolCallStart,
                onToolCallResult,
                onHandoverStatus,
            );
            assistantText = result.assistantText;
            streamDone = streamDone || result.streamDone;
        }

        if (done) {
            break;
        }
    }

    if (buffer.length > 0) {
        const trailingLines = buffer.endsWith('\n') ? buffer.split('\n') : [...buffer.split('\n')];
        const result = processSseLines(
            trailingLines,
            assistantText,
            onDelta,
            onTurnPersisted,
            onContentReplacement,
            onLeadForm,
            onToolCallStart,
            onToolCallResult,
            onHandoverStatus,
        );
        assistantText = result.assistantText;
        streamDone = streamDone || result.streamDone;
    }

    const displayText = stripInternalMarkersForDisplay(assistantText);

    if (streamDone) {
        onDone?.(displayText);
    }

    return displayText;
}
